import React from 'react';

import { InfoOutlined, Search } from '@mui/icons-material';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Tooltip } from '@mui/material';
import { action, observable, runInAction, makeObservable } from 'mobx';
import {  observer } from 'mobx-react';
import NumeroDeProcesso from '../misc/NumeroDeProcesso';
import ProcessoService from '../services/ProcessoService';
import MensagemStore from '../store/MensagemStore';
import RouterStore from '../store/RouterStore';
import { apiErroPadraoJaExibido, apresentarApiErroPadrao } from '../utils/ApiErroPadraoUtils';

export const CONSULTA_PROCESSO_ABA_GE_REQUEST_KEY = 'consulta-processo-aba-ge-request';
export const CONSULTA_PROCESSO_ABA_GE_RESPONSE_KEY = 'consulta-processo-aba-ge-response';

interface IPropsConsultaProcesso {
    consultarViaAbaGe?: boolean;
}


class ConsultaProcesso extends React.Component<IPropsConsultaProcesso> {
    open = false;
    isLoading = false;
    numeroProcesso = {
        numero: '',
        digito: '',
        ano: '',
        orgao: '',
        regional: 'string',
        vara: ''
    };

    handleClose = () => {
        this.open = false;
    };

    handleConsultaProcesso = () => {
        this.isLoading = false;
        this.open = true;
    };

    atualizaNumeroProcesso = (numero) => {
        this.numeroProcesso = numero;
    };

    numeroProcessoCompleto = () => {
        const numeroNormalizado = this.numeroProcesso.numero
            ? this.numeroProcesso.numero.padStart(7, '0')
            : '';

        if (
            numeroNormalizado.length === 7 &&
            this.numeroProcesso.digito?.length === 2 &&
            this.numeroProcesso.ano?.length === 4 &&
            this.numeroProcesso.orgao?.length === 1 &&
            this.numeroProcesso.regional?.length === 2 &&
            this.numeroProcesso.vara?.length === 4
        ) {
            return `${numeroNormalizado}-${this.numeroProcesso.digito}.${this.numeroProcesso.ano}.${this.numeroProcesso.orgao}.${this.numeroProcesso.regional}.${this.numeroProcesso.vara}`;
        }

        return '';
    }

    processoJaAbertoNoEspelho = (numeroCompleto: string) => {
        const numeroProcessoEspelho = RouterStore.dadosProcessoEspelho?.numero;
        if (!numeroCompleto || !numeroProcessoEspelho) {
            return false;
        }

        return numeroCompleto.replace(/\D/g, '') === numeroProcessoEspelho.replace(/\D/g, '');
    }

    abrirRotaConsultaEspelho = (rota: string) => {
        if (RouterStore.erroCarregamentoEspelho) {
            RouterStore.redirecionamentoExterno(rota);
            return;
        }

        RouterStore.abrirJanela(rota);
    }

    abrirEspelhoPorNumeroCompleto = async () => {
        const numeroCompleto = this.numeroProcessoCompleto();
        if (!numeroCompleto) {
            MensagemStore.apresentarAviso('Não foi possível consultar na aba do GE. Informe o número completo para abrir o espelho do processo.');
            return false;
        }

        try {
            const idProcesso = await ProcessoService.idProcessoPorNumero(numeroCompleto);
            this.abrirRotaConsultaEspelho('/espelhoProcesso/' + idProcesso);
            return true;
        } catch (error) {
            console.error('Erro ao abrir espelho por número do processo:', error);
            if (!apiErroPadraoJaExibido(error) && !apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarAviso('Processo não existe ou não foi localizado.');
            }
            return false;
        }
    }

    pesquisarProcessoViaAbaGe = async () => {
        const numeroCompleto = this.numeroProcessoCompleto();
        if (!numeroCompleto) {
            MensagemStore.apresentarAviso('Informe o número completo do processo para consultar a partir do espelho do processo.');
            return false;
        }

        if (this.processoJaAbertoNoEspelho(numeroCompleto)) {
            MensagemStore.apresentarAviso('O processo informado já está aberto no espelho do processo.');
            return false;
        }

        const id = `${Date.now()}-${Math.random()}`;

        const resposta = await new Promise<any>((resolve) => {
            let finalizado = false;

            const finalizar = (resultado: any) => {
                if (finalizado) {
                    return;
                }
                finalizado = true;
                window.removeEventListener('storage', listener);
                resolve(resultado);
            };

            const listener = (event: StorageEvent) => {
                if (event.key !== CONSULTA_PROCESSO_ABA_GE_RESPONSE_KEY || !event.newValue) {
                    return;
                }

                const response = JSON.parse(event.newValue);
                if (response?.id === id) {
                    finalizar(response);
                }
            };

            window.addEventListener('storage', listener);
            localStorage.setItem(CONSULTA_PROCESSO_ABA_GE_REQUEST_KEY, JSON.stringify({
                id,
                numeroProcesso: this.numeroProcesso
            }));

            setTimeout(() => finalizar(null), 5000);
        });

        if (resposta?.encontrado) {
            if (RouterStore.erroCarregamentoEspelho) {
                window.close();
                return true;
            }
            MensagemStore.apresentarInformacao('Processo localizado no gabinete e filtrado na aba do Gabinete Eletrônico.', 'top');
            return true;
        }

        return await this.abrirEspelhoPorNumeroCompleto();
    }

    pesquisarProcesso = async () => {
        runInAction(() => {
            this.isLoading = true;
        });
        try {
            const pesquisaComSucesso = this.props.consultarViaAbaGe
                ? await this.pesquisarProcessoViaAbaGe()
                : await ProcessoService.buscarProcessoNoGabinete(this.numeroProcesso);
            if (pesquisaComSucesso) {
                this.handleClose();
            }
        } catch (error) {
            console.error('Erro ao pesquisar processo:', error);
            if (!apiErroPadraoJaExibido(error) && !apresentarApiErroPadrao(error)) {
                MensagemStore.apresentarAviso('Processo não existe. Por favor verifique o número digitado e tente novamente.');
            }
        } finally {
            runInAction(() => {
                this.isLoading = false;
            });
        }
    }

    constructor(props: IPropsConsultaProcesso) {
        super(props);

        makeObservable(this, {
            open: observable,
            isLoading: observable,
            numeroProcesso: observable,
            handleClose: action,
            handleConsultaProcesso: action,
            atualizaNumeroProcesso: action,
            pesquisarProcesso: action
        });
    }

    render() {
        return (
            <div>
                <Tooltip title="Consultar processo">
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                        <IconButton onClick={this.handleConsultaProcesso} aria-label='Abrir consulta de processo por número.'>
                            <Search style={{ cursor: 'pointer', color: 'white' }} />
                        </IconButton>
                    </div>
                </Tooltip>
                <Dialog
                    open={this.open}
                    onClose={this.handleClose}
                >
                    <DialogTitle>
                        Consulta por Número do Processo
                    </DialogTitle>
                    <DialogContent>
                        <NumeroDeProcesso getNumeroProcesso={this.atualizaNumeroProcesso} callback={this.pesquisarProcesso} white={false} hasFocus={true}/>
                        <div style={{ display: 'flex', alignItems: 'center', fontSize: '10pt', color: '#0d47a1', fontStyle: 'bold', gap: 5}}>
                            <InfoOutlined/>
                            {this.props.consultarViaAbaGe
                                ? 'No espelho do processo, informe o número completo. Se o processo estiver no gabinete, ele será aberto no escaninho; caso contrário, será aberto outro espelho do processo.'
                                : 'A pesquisa por parte do número busca nos processos do gabinete; para consultar bases externas(outros sistemas), informe o número completo.'}
                        </div>
                    </DialogContent>
                    <DialogActions>
                        <Button variant='outlined' color='secondary' onClick={this.handleClose} disabled={this.isLoading}>
                            Fechar
                        </Button>
                        <Button variant='contained' color='primary' onClick={this.pesquisarProcesso} disabled={this.isLoading}>
                            {this.isLoading ? <CircularProgress size={24} /> : 'Pesquisar'}
                        </Button>
                    </DialogActions>
                </Dialog>
            </div>
        );
    }
}

export default observer(ConsultaProcesso);
